import { Box, Flex } from 'grid-styled'
import * as React from 'react'
import Layout from '../../components/Layout'
import { graphql, Link } from 'gatsby'
import Img from 'gatsby-image'

import CTA from '../../components/CTA'

import {
  Announcement,
  Cards,
  Container,
  Hero,
  HeroContent,
  HeroFigure,
  HeroMain,
  SplitSection,
  SplitFigure,
  SplitContent,
  Text,
  ReadMore
} from '../../components/styles'
import { Button } from '../../components/Button'
import FAQs from '../../components/FAQs'
import Form from '../../components/Form'
import { AdCases } from '../../components/case-studies/AdCases'
import Arrow from '../../components/Arrow'
import { Helmet } from 'react-helmet'

export const faqsInfo = [
  {
    title: 'Common PPC questions'
  },
  {
    answer: `It sure does. We regularly see the impact it has on our clients. We literally see businesses transform in a matter of weeks by taking their number one issue off the table: They can now get more sales whenever they like.`,
    question: 'Does PPC Really Work?'
  },
  {
    answer: `Everything depends on your unique business setup and industry. We have yet to see a customer who could not benefit in some way from PPC advertising. However, just because it works doesn't mean it's the best strategy for you. Get in touch with us today and we can procide free guidance on exactly what is best for your business.`,
    question: 'Do I need PPC for my business?'
  },
  {
    answer: `Not months, not even weeks, but days. Once the campaign is set up and running you'll start seeing more and more traffic flooding your website. This stuff works. And it doesn't take years or tens of thousands in down payment like traditional marketing methods. If you're impatient like us, and you want to see the results as quickly as possible, this may be for you.`,
    question: 'How long until I see results?'
  },
  {
    answer: `Every industry will be different in this regard. If you're selling $500,000 homes, you can expect the competition to be a bit more fierce and the cost per click to be a bit higher. If you're selling $50 plants you can expect it a bit more. Reach out and tell us a bit about your industry and we'll get back to you with the estimated cost you can expect to pay for a lead.`,
    question: 'How much does it cost?'
  },
  {
    answer:
      'Organic results are the results that appear in search engines, for free, based on an algorithm. Paid (or ‘inorganic’) search results appear at the top or side of a page – they are links that advertisers pay to appear on search engines.',
    question: 'What is the difference between organic and paid results?'
  },
  {
    answer:
      'We have a number of KPIs that we use to measure the success of our efforts, some of the key analytics are "Cost Per Click" and "Number of clicks" and "Conversion rate". This is the holy trinity of PPC KPIs, they provide us with the information we need to strengthen and improve your Google Ad strategies.',
    question: 'How do you measure the success of your results?'
  },
  {
    answer:
      'We will regularly send you reports that contain historical data, trends, analysis, our KPI measurements. And the results you have gotten from your campaigns. If the information if useful to you, it will be there. We string to be as transparent as possible in everything we do, we believe this is a business practice that fosters trust and strong relationships.',
    question: 'What reports do I get?'
  }
]

/* prettier-ignore */
interface IProps {
  data: {
    hero: {
      childImageSharp: {
        fluid: object
      }
    };
    bww: {
      childImageSharp: {
        fluid: object
      }
    };
    guys: {
      childImageSharp: {
        fluid: object
      }
    };
  }
}

export default ({ data }: IProps) => (
  <Layout>
    <Helmet>
      <title>Google Ads - PPC Search & Display Ads - Turboclicks</title>
      <meta
        name="description"
        content="Learn how to double your business sales with Google Ads PPC campaigns. Talk to our experts today to find out what's right for your business."
      />
    </Helmet>
    <Hero>
      <Container>
        <HeroContent>
          <HeroMain>
            <h1>Google "pay per click" advertising</h1>
            <p>
              Get in front of customers that are searching for your business
              with Google Ads.
            </p>
            <Link to="/contact#form">
              <Button inverted={true}>
                Show me how to grow my business sales
              </Button>
            </Link>
            <Box mt={3}>
              <Announcement to="/contact">
                Speak with a PPC specialist
                <Arrow rotate="-90deg" />
              </Announcement>
            </Box>
          </HeroMain>

          <HeroFigure>
            <Img
              fluid={data.hero.childImageSharp.fluid}
              alt="Brisbane SEO Servces"
            />
          </HeroFigure>
        </HeroContent>
      </Container>
    </Hero>

    <FAQs data={faqsInfo} flushTop={true} />

    <Box mb={6} />

    <AdCases image={data.bww.childImageSharp.fluid} />

    <Box mt={4}>
      <Container
        image="//truework.com//images/customer-stories/background-c96746d7.jpg"
        full={true}
        padY={true}
      >
        <Container medium={true}>
          <h2>
            100% results based. Only pay for the clicks that bring traffic to
            your website.
          </h2>
        </Container>
      </Container>
    </Box>

    <Container padY={true} medium={true}>
      <h3>Why PPC Advertising?</h3>
      <Text size="large">
        Go search for something right now. You'll see that at the very top of
        the page is a an ad, it looks just like every other search result. But
        that business is paying to be put in front of customers who are ready to
        buy. These are qualified leads looking for your service.
      </Text>

      <Text size="large">
        Pay Per Click (PPC) campaigns are essentially online advertising
        campaigns where you are only charged when a customer who is ready to buy
        clicks on your ad. If you can't wait for a Search Engine Optimisation
        campaign to push your business to the top, Google Search Ads can help
        you skip the queue and take your business to #1 overnight.
      </Text>

      <Text size="large">
        The most effective Google Ad campaigns are the ones which have the most
        time and experience invested in them. Everything should be researched,
        monitored and adjusted in response to market and competition
        fluctuations. We take the hassle out of this by managing everything for
        you, let our "Google qualified" individuals create, monitor and optimise
        your campaigns to generate the best results for your business.
      </Text>
      <Link to="/contact#form">
        <Button inverted={true}>Help me double my sales</Button>
      </Link>
    </Container>

    <Container medium={true}>
      <SplitSection alternate={true} padY={true}>
        <SplitFigure>
          <Img
            fluid={data.guys.childImageSharp.fluid}
            alt="Turboclicks happy partners without contracts"
          />
        </SplitFigure>
        <SplitContent>
          <h2>Double your sales</h2>
          <p>
            We've seen the results of effective Google Ads campaigns. Many of
            our clients now rely on it as their primary form of lead generation.
            Give us a call to see what effect it can have for your business.
          </p>
          <ReadMore to="/contact">
            Boost your business growth <Arrow rotate="-90deg" />
          </ReadMore>
        </SplitContent>
      </SplitSection>
    </Container>

    <CTA
      inverted={true}
      title="Want to find out more?"
      subtitle="We'd love to chat."
      button="Speak to a specialist"
    />

    <Form />
  </Layout>
)

export const query = graphql`
  query GoogleAdsPageQuery {
    hero: file(relativePath: { eq: "google-adwords-phone.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1000) {
          ...GatsbyImageSharpFluid_tracedSVG
        }
      }
    }

    bww: file(
      relativePath: { eq: "turboclicks-brisbanewhalewatching-case.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 1000) {
          ...GatsbyImageSharpFluid
        }
      }
    }

    guys: file(relativePath: { eq: "guys.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1000) {
          ...GatsbyImageSharpFluid
        }
      }
    }
  }
`
